// The sounds of the town, all made in the browser (there are no sound files):
// an engine that rises with speed, a horn, rain, footsteps, the murmur of a
// crowd and crickets at night. Browsers only allow sound after the player has
// clicked or pressed a key, so it starts on the first of those.
export class Sound {
  constructor() {
    this.ctx = null;
    this.on = true;
    try {
      this.on = localStorage.getItem('abakwa-sound') !== 'off';
    } catch {
      // no storage: sound stays on for this visit
    }
    this.step = 0;
    const start = () => this.start();
    for (const type of ['pointerdown', 'keydown', 'touchstart']) window.addEventListener(type, start, { once: true });
  }

  start() {
    if (this.ctx) return;
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    this.ctx = ctx;
    this.master = ctx.createGain();
    this.master.gain.value = this.on ? 1 : 0;
    this.master.connect(ctx.destination);

    // two seconds of hiss, the raw stuff of rain, footsteps and voices
    const noise = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const data = noise.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
    this.noise = noise;
    const hiss = (type, freq, q) => {
      const src = ctx.createBufferSource();
      src.buffer = noise;
      src.loop = true;
      const filter = ctx.createBiquadFilter();
      filter.type = type;
      filter.frequency.value = freq;
      filter.Q.value = q;
      const gain = ctx.createGain();
      gain.gain.value = 0;
      src.connect(filter).connect(gain).connect(this.master);
      src.start();
      return gain;
    };
    this.rain = hiss('highpass', 1800, 0.4);
    this.crowd = hiss('bandpass', 520, 0.8);

    // the engine: a rough tone through a filter that opens as the revs rise
    this.engine = ctx.createOscillator();
    this.engine.type = 'sawtooth';
    this.engine.frequency.value = 50;
    this.engineFilter = ctx.createBiquadFilter();
    this.engineFilter.type = 'lowpass';
    this.engineFilter.frequency.value = 300;
    this.engineGain = ctx.createGain();
    this.engineGain.gain.value = 0;
    this.engine.connect(this.engineFilter).connect(this.engineGain).connect(this.master);
    this.engine.start();

    // crickets: a high note switched on and off many times a second
    const chirp = ctx.createOscillator();
    chirp.frequency.value = 4300;
    const pulse = ctx.createOscillator();
    pulse.type = 'square';
    pulse.frequency.value = 17;
    const depth = ctx.createGain();
    depth.gain.value = 0.5;
    const gate = ctx.createGain();
    gate.gain.value = 0.5;
    pulse.connect(depth).connect(gate.gain);
    this.crickets = ctx.createGain();
    this.crickets.gain.value = 0;
    chirp.connect(gate).connect(this.crickets).connect(this.master);
    chirp.start();
    pulse.start();
    // recorded beds (CC0, public/assets/sound): rain, crickets, traffic and a stream; each takes
    // over from its synthesised stand-in the moment it has decoded
    this.samples = {};
    // a generator running in the quarter while the light is out: a rough low tone through a filter
    const gen = ctx.createOscillator();
    gen.type = 'sawtooth';
    gen.frequency.value = 54;
    const genFilter = ctx.createBiquadFilter();
    genFilter.type = 'lowpass';
    genFilter.frequency.value = 220;
    const genWobble = ctx.createOscillator();
    genWobble.frequency.value = 9;
    const wobbleDepth = ctx.createGain();
    wobbleDepth.gain.value = 3;
    genWobble.connect(wobbleDepth).connect(gen.frequency);
    this.generator = ctx.createGain();
    this.generator.gain.value = 0;
    gen.connect(genFilter).connect(this.generator).connect(this.master);
    gen.start();
    genWobble.start();
    for (const [name, file] of Object.entries({ rain: 'rain.ogg', crickets: 'crickets.mp3', traffic: 'traffic.ogg', stream: 'stream.ogg' })) this.loadLoop(name, `assets/sound/${file}`);
  }

  async loadLoop(name, url) {
    try {
      const res = await fetch(url);
      if (!res.ok) return;
      const buffer = await this.ctx.decodeAudioData(await res.arrayBuffer());
      if (!this.ctx) return;
      const src = this.ctx.createBufferSource();
      src.buffer = buffer;
      src.loop = true;
      const gain = this.ctx.createGain();
      gain.gain.value = 0;
      src.connect(gain).connect(this.master);
      src.start();
      this.samples[name] = gain;
    } catch (e) {
      console.warn(`sound ${name} did not load`, e);
    }
  }

  // Turns all sound on or off. Returns whether it is now on.
  // A short rising sting for a mission passed.
  chime() {
    try {
      const ctx = this.ctx || this.context || (this.ensure && this.ensure());
      if (!ctx || !this.on) return;
      const now = ctx.currentTime;
      for (const [k, f] of [[0, 523.25], [0.14, 659.25], [0.28, 783.99], [0.42, 1046.5]]) {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = 'triangle';
        o.frequency.value = f;
        g.gain.setValueAtTime(0.0001, now + k);
        g.gain.exponentialRampToValueAtTime(0.18, now + k + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, now + k + 0.5);
        o.connect(g).connect(this.master || ctx.destination);
        o.start(now + k);
        o.stop(now + k + 0.55);
      }
    } catch {
      // no audio: the card alone
    }
  }

  toggle() {
    this.on = !this.on;
    try {
      localStorage.setItem('abakwa-sound', this.on ? 'on' : 'off');
    } catch {
      // not remembered
    }
    if (this.ctx) this.master.gain.setTargetAtTime(this.on ? 1 : 0, this.ctx.currentTime, 0.05);
    return this.on;
  }

  // The light going: a low thud and the hum of everything electric dying away.
  cut() {
    if (!this.ctx || !this.on) return;
    const ctx = this.ctx;
    const t = ctx.currentTime;
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(110, t);
    o.frequency.exponentialRampToValueAtTime(38, t + 0.5);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.25, t + 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.9);
    o.connect(g).connect(this.master);
    o.start(t);
    o.stop(t + 1);
  }

  // A taxi's two-note horn.
  horn(bike) {
    if (!this.ctx || !this.on) return;
    const t = this.ctx.currentTime;
    for (const freq of bike ? [620] : [415, 520]) {
      const o = this.ctx.createOscillator();
      o.type = 'square';
      o.frequency.value = freq;
      const g = this.ctx.createGain();
      g.gain.setValueAtTime(0, t);
      g.gain.linearRampToValueAtTime(0.09, t + 0.02);
      g.gain.setValueAtTime(0.09, t + 0.28);
      g.gain.linearRampToValueAtTime(0, t + 0.34);
      o.connect(g).connect(this.master);
      o.start(t);
      o.stop(t + 0.36);
    }
  }

  footstep() {
    const src = this.ctx.createBufferSource();
    src.buffer = this.noise;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 500 + Math.random() * 300;
    const g = this.ctx.createGain();
    const t = this.ctx.currentTime;
    g.gain.setValueAtTime(0.12, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.09);
    src.connect(filter).connect(g).connect(this.master);
    src.start(t, Math.random() * 1.5, 0.1);
  }

  // Call every frame. s: { dt, driving, bike, speed (m/s), walking (m/s on foot,
  // 0 if not), rain 0-1, night 0-1, people (townspeople in sight), traffic 0-1 (cars close by),
  // water 0-1 (a stream close by) }.
  update(s) {
    if (!this.ctx || !this.on) return;
    const t = this.ctx.currentTime;
    const to = (param, value) => param.setTargetAtTime(value, t, 0.12);
    to(this.engineGain.gain, s.driving ? (s.bike ? 0.035 : 0.05) : 0);
    to(this.engine.frequency, (s.bike ? 70 : 48) + s.speed * (s.bike ? 9 : 6));
    to(this.engineFilter.frequency, 260 + s.speed * 45);
    const sm = this.samples || {};
    to(this.rain.gain, sm.rain ? 0 : s.rain * 0.16);
    if (sm.rain) to(sm.rain.gain, s.rain * 0.55);
    to(this.crowd.gain, Math.min(0.05, s.people * 0.0012) * (1 - s.night));
    to(this.crickets.gain, sm.crickets ? 0 : s.night * (1 - s.rain) * 0.012);
    if (sm.crickets) to(sm.crickets.gain, s.night * (1 - s.rain) * 0.35);
    if (sm.traffic) to(sm.traffic.gain, (s.traffic || 0) * 0.22 * (s.driving ? 0.5 : 1));
    if (sm.stream) to(sm.stream.gain, (s.water || 0) * 0.3 * (1 - s.rain * 0.5));
    if (this.generator) to(this.generator.gain, s.generator ? 0.028 : 0);
    if (s.walking > 0.5) {
      if (s.footPhase != null) {
        // a step each time a foot lands: at the start and the middle of the walking clip
        const half = Math.floor(s.footPhase * 2);
        if (half !== this.lastHalf) {
          this.lastHalf = half;
          this.footstep();
        }
      } else {
        this.step -= s.dt * (1.6 + s.walking * 0.45);
        if (this.step <= 0) {
          this.step = 1;
          this.footstep();
        }
      }
    } else this.lastHalf = -1;
  }
}
